import React, { useEffect, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Sparkles,
  Wand2,
  RotateCw,
  Check,
  X,
  AlertTriangle,
  ArrowRight,
  GitCompare,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { getGetExtractionStatusQueryKey } from "@workspace/api-client-react";

function getBaseUrl(): string {
  return import.meta.env.BASE_URL?.replace(/\/$/, "") ?? "";
}

type FilterType = "all" | "batch" | "dateRange" | "failed";
type Mode = "overwrite" | "candidate";

interface BatchOption {
  uploadBatch: string;
  imageCount: number;
  earliest: string;
  latest: string;
}

interface CandidateRow {
  id: number;
  recipeId: number;
  currentTitle: string | null;
  candidateTitle: string;
  status: string;
  errorMessage: string | null;
  createdAt: string;
  resolvedAt: string | null;
}

function formatBatchLabel(b: BatchOption): string {
  const d = new Date(b.latest);
  const date = d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  return `${date} · ${b.imageCount} image${b.imageCount === 1 ? "" : "s"}`;
}

export function BatchReextractSection() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [mode, setMode] = useState<Mode>("candidate");
  const [filterType, setFilterTypeState] = useState<FilterType>("failed");

  // Candidate mode requires an existing recipe to compare against, but
  // failed-extraction images by definition have no recipe yet — every
  // candidate job for them would error out and produce no compare row.
  // Force the mode to overwrite (which falls back to fresh extraction +
  // recipe creation) whenever the filter is `failed`, and lock the
  // candidate button so users can't pick a combination the backend will
  // refuse.
  const candidateModeDisabled = filterType === "failed";
  const setFilterType = (next: FilterType) => {
    setFilterTypeState(next);
    if (next === "failed" && mode === "candidate") {
      setMode("overwrite");
    }
  };
  React.useEffect(() => {
    if (filterType === "failed" && mode === "candidate") {
      setMode("overwrite");
    }
  }, [filterType, mode]);
  const [batches, setBatches] = useState<BatchOption[]>([]);
  const [selectedBatches, setSelectedBatches] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [candidates, setCandidates] = useState<CandidateRow[]>([]);
  const [candidatesLoading, setCandidatesLoading] = useState(true);
  const [openCandidateId, setOpenCandidateId] = useState<number | null>(null);
  const [resolvingIds, setResolvingIds] = useState<Set<number>>(new Set());

  const loadBatches = useCallback(async () => {
    try {
      const r = await fetch(`${getBaseUrl()}/api/reextract/batches`);
      if (!r.ok) return;
      const data = (await r.json()) as { batches: BatchOption[] };
      setBatches(data.batches);
    } catch {
      // silent — batches dropdown will just be empty
    }
  }, []);

  const loadCandidates = useCallback(async () => {
    try {
      const r = await fetch(
        `${getBaseUrl()}/api/reextract/candidates?status=pending`,
      );
      if (!r.ok) return;
      const data = (await r.json()) as { candidates: CandidateRow[] };
      setCandidates(data.candidates);
    } catch {
      // silent
    } finally {
      setCandidatesLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBatches();
    loadCandidates();
  }, [loadBatches, loadCandidates]);

  // Poll candidates while there's queue activity so newly-finished
  // candidate jobs surface without a manual refresh.
  useEffect(() => {
    const id = setInterval(loadCandidates, 5000);
    return () => clearInterval(id);
  }, [loadCandidates]);

  const handleStart = async () => {
    if (filterType === "batch" && selectedBatches.length === 0) {
      toast({
        title: "Pick at least one batch",
        variant: "destructive",
      });
      return;
    }
    if (filterType === "dateRange" && (!dateFrom || !dateTo)) {
      toast({
        title: "Pick a date range",
        variant: "destructive",
      });
      return;
    }
    const filter: Record<string, unknown> = { type: filterType };
    if (filterType === "batch") filter.batches = selectedBatches;
    if (filterType === "dateRange") {
      // Make `dateTo` inclusive of the entire day.
      filter.dateFrom = new Date(dateFrom + "T00:00:00").toISOString();
      filter.dateTo = new Date(dateTo + "T23:59:59.999").toISOString();
    }

    setSubmitting(true);
    try {
      const r = await fetch(`${getBaseUrl()}/api/reextract`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mode, filter }),
      });
      const data = (await r.json()) as {
        queued?: number;
        alreadyInFlight?: number;
        skipped?: number;
        failed?: number;
        total?: number;
        error?: string;
      };
      if (!r.ok) {
        toast({
          title: "Couldn't start re-extraction",
          description: data.error ?? "Unknown error",
          variant: "destructive",
        });
        return;
      }
      const parts: string[] = [];
      if (data.queued) parts.push(`${data.queued} queued`);
      if (data.alreadyInFlight)
        parts.push(`${data.alreadyInFlight} already in flight`);
      if (data.skipped) {
        // Backend reports images filtered out because candidate mode
        // requires an existing linked recipe. Spell that out so admins
        // running broad filters understand why the count differs from
        // their selection.
        parts.push(
          `${data.skipped} skipped (no existing recipe — use Overwrite to extract them fresh)`,
        );
      }
      if (data.failed) parts.push(`${data.failed} failed to enqueue`);
      const hasIssues = (data.failed ?? 0) > 0 || (data.skipped ?? 0) > 0;
      toast({
        title:
          (data.queued ?? 0) > 0
            ? "Re-extraction started"
            : "No new jobs queued",
        description:
          parts.length > 0 ? parts.join(" · ") : "All selected images are already in flight.",
        variant: hasIssues && (data.queued ?? 0) === 0 ? "destructive" : undefined,
      });
      queryClient.invalidateQueries({ queryKey: getGetExtractionStatusQueryKey() });
      // Allow a few seconds for the worker to write candidates, then refresh.
      setTimeout(loadCandidates, 2000);
    } catch (err) {
      toast({
        title: "Network error",
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleAccept = async (id: number) => {
    setResolvingIds((prev) => new Set(prev).add(id));
    try {
      const r = await fetch(
        `${getBaseUrl()}/api/reextract/candidates/${id}/accept`,
        { method: "POST" },
      );
      const data = (await r.json()) as { success?: boolean; error?: string };
      if (!r.ok || !data.success) {
        toast({
          title: "Accept failed",
          description: data.error ?? "Unknown error",
          variant: "destructive",
        });
        return;
      }
      toast({ title: "Candidate accepted", description: "Recipe updated." });
      setOpenCandidateId(null);
      queryClient.invalidateQueries({ queryKey: ["listRecipes"] });
      loadCandidates();
    } finally {
      setResolvingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const handleReject = async (id: number) => {
    setResolvingIds((prev) => new Set(prev).add(id));
    try {
      const r = await fetch(
        `${getBaseUrl()}/api/reextract/candidates/${id}/reject`,
        { method: "POST" },
      );
      const data = (await r.json()) as { success?: boolean; error?: string };
      if (!r.ok || !data.success) {
        toast({
          title: "Reject failed",
          description: data.error ?? "Unknown error",
          variant: "destructive",
        });
        return;
      }
      toast({ title: "Candidate rejected" });
      setOpenCandidateId(null);
      loadCandidates();
    } finally {
      setResolvingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const toggleBatch = (b: string) => {
    setSelectedBatches((prev) =>
      prev.includes(b) ? prev.filter((x) => x !== b) : [...prev, b],
    );
  };

  return (
    <>
      <Card className="bg-purple-50/60 border-purple-200/60">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-purple-600" />
            <CardTitle className="text-base font-semibold">
              Batch Re-Extraction
            </CardTitle>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Re-run AI extraction on existing images. Files are not re-uploaded.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Mode toggle */}
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1.5">
              Result handling
            </label>
            <div className="inline-flex border rounded-lg overflow-hidden">
              <button
                onClick={() => setMode("candidate")}
                disabled={candidateModeDisabled}
                title={
                  candidateModeDisabled
                    ? "Failed images have no recipe yet — candidate mode needs an existing recipe to compare against. Use Overwrite to create a fresh recipe."
                    : undefined
                }
                className={`px-3 py-1.5 text-sm transition-colors ${
                  candidateModeDisabled
                    ? "bg-muted text-muted-foreground cursor-not-allowed opacity-60"
                    : mode === "candidate"
                    ? "bg-purple-600 text-white"
                    : "bg-background hover:bg-muted"
                }`}
              >
                Candidate (review)
              </button>
              <button
                onClick={() => setMode("overwrite")}
                className={`px-3 py-1.5 text-sm transition-colors border-l ${
                  mode === "overwrite"
                    ? "bg-purple-600 text-white"
                    : "bg-background hover:bg-muted"
                }`}
              >
                Overwrite in place
              </button>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">
              {candidateModeDisabled
                ? "Failed images have no existing recipe to compare against — candidate mode is unavailable for this filter."
                : mode === "candidate"
                ? "Stores the new extraction so you can compare and accept/reject."
                : "Replaces the existing recipe data immediately. Cannot be undone."}
            </p>
          </div>

          {/* Filter type */}
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1.5">
              Which images
            </label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as FilterType)}
              className="w-full sm:w-72 h-9 rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="failed">Only failed extractions</option>
              <option value="batch">By upload batch</option>
              <option value="dateRange">By upload date range</option>
              <option value="all">All images (be careful)</option>
            </select>
          </div>

          {/* Conditional inputs */}
          {filterType === "batch" && (
            <div>
              <label className="text-xs font-medium text-muted-foreground block mb-1.5">
                Batches ({selectedBatches.length} selected)
              </label>
              <div className="max-h-40 overflow-y-auto border rounded-md bg-background divide-y">
                {batches.length === 0 && (
                  <p className="text-xs text-muted-foreground p-3">
                    No upload batches available.
                  </p>
                )}
                {batches.map((b) => (
                  <label
                    key={b.uploadBatch}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-muted/40 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedBatches.includes(b.uploadBatch)}
                      onChange={() => toggleBatch(b.uploadBatch)}
                    />
                    <span>{formatBatchLabel(b)}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {filterType === "dateRange" && (
            <div className="grid grid-cols-2 gap-3 max-w-md">
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1.5">
                  From
                </label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1.5">
                  To
                </label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </div>
            </div>
          )}

          <Button
            onClick={handleStart}
            disabled={submitting}
            className="gap-2 bg-purple-600 hover:bg-purple-700 text-white"
          >
            {submitting ? (
              <RotateCw className="w-4 h-4 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4" />
            )}
            Start Re-Extraction
          </Button>

          {/* Pending candidates list */}
          {(candidatesLoading || candidates.length > 0) && (
            <div className="pt-3 border-t border-purple-200/60">
              <div className="flex items-center gap-2 mb-2">
                <GitCompare className="w-4 h-4 text-purple-600" />
                <span className="text-sm font-semibold">
                  Pending candidates
                </span>
                <Badge variant="secondary" className="ml-1">
                  {candidates.length}
                </Badge>
              </div>
              {candidatesLoading ? (
                <p className="text-xs text-muted-foreground">Loading…</p>
              ) : (
                <div className="space-y-1.5">
                  {candidates.map((c) => {
                    const titleChanged =
                      c.currentTitle && c.currentTitle !== c.candidateTitle;
                    const isResolving = resolvingIds.has(c.id);
                    return (
                      <div
                        key={c.id}
                        className="flex flex-col sm:flex-row sm:items-center gap-2 px-3 py-2 bg-white border rounded-md"
                      >
                        <div className="flex-1 min-w-0 text-sm">
                          {titleChanged ? (
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="line-through text-muted-foreground">
                                {c.currentTitle}
                              </span>
                              <ArrowRight className="w-3 h-3 text-muted-foreground" />
                              <span className="font-medium">
                                {c.candidateTitle}
                              </span>
                            </div>
                          ) : (
                            <span className="font-medium">
                              {c.candidateTitle}
                            </span>
                          )}
                          <p className="text-[11px] text-muted-foreground">
                            Created{" "}
                            {new Date(c.createdAt).toLocaleString("en-US", {
                              month: "short",
                              day: "numeric",
                              hour: "numeric",
                              minute: "2-digit",
                            })}
                          </p>
                        </div>
                        <div className="flex gap-1.5">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setOpenCandidateId(c.id)}
                            disabled={isResolving}
                          >
                            <GitCompare className="w-3.5 h-3.5 mr-1" />
                            Compare
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleReject(c.id)}
                            disabled={isResolving}
                          >
                            <X className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                  {candidates.length === 0 && (
                    <p className="text-xs text-muted-foreground">
                      None pending.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {openCandidateId !== null && (
        <CandidateCompareDialog
          candidateId={openCandidateId}
          onClose={() => setOpenCandidateId(null)}
          onAccept={() => handleAccept(openCandidateId)}
          onReject={() => handleReject(openCandidateId)}
          resolving={resolvingIds.has(openCandidateId)}
        />
      )}
    </>
  );
}

// ─── Compare dialog ────────────────────────────────────────────────────────

interface IngredientShape {
  sortOrder?: number;
  sort_order?: number;
  amount?: string | null;
  unit?: string | null;
  item: string;
  prepNotes?: string | null;
  prep_notes?: string | null;
  sectionHeader?: string | null;
  section_header?: string | null;
}

interface CandidatePayload {
  candidate: {
    id: number;
    recipeId: number;
    status: string;
    candidateData: {
      recipe: {
        title: string;
        author_source?: string | null;
        category?: string | null;
        cuisine?: string | null;
        prep_time?: string | null;
        cook_time?: string | null;
        total_time?: string | null;
        yield_servings?: string | null;
        difficulty?: string | null;
        directions: string;
        serving_suggestions?: string | null;
        handwritten_notes?: string | null;
        source_type?: string | null;
        ingredients?: IngredientShape[];
      };
    };
  };
  currentRecipe: {
    title: string;
    authorSource?: string | null;
    category?: string | null;
    cuisine?: string | null;
    prepTime?: string | null;
    cookTime?: string | null;
    totalTime?: string | null;
    yieldServings?: string | null;
    difficulty?: string | null;
    directions: string;
    servingSuggestions?: string | null;
    handwrittenNotes?: string | null;
    sourceType?: string | null;
    ingredients: IngredientShape[];
  };
  sourceImages: { id: number; filename: string; originalName: string }[];
}

function CandidateCompareDialog({
  candidateId,
  onClose,
  onAccept,
  onReject,
  resolving,
}: {
  candidateId: number;
  onClose: () => void;
  onAccept: () => void;
  onReject: () => void;
  resolving: boolean;
}) {
  const [data, setData] = useState<CandidatePayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(
          `${getBaseUrl()}/api/reextract/candidates/${candidateId}`,
        );
        const json = (await r.json()) as CandidatePayload | { error?: string };
        if (cancelled) return;
        if (!r.ok) {
          setError((json as { error?: string }).error ?? "Failed to load");
          return;
        }
        setData(json as CandidatePayload);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [candidateId]);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Compare extraction</DialogTitle>
          <DialogDescription>
            Review the new AI extraction next to the current recipe. Accept to
            replace, reject to discard.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-12">
            <RotateCw className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {error && (
          <div className="bg-destructive/5 text-destructive border border-destructive/20 rounded-md p-3 text-sm flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {data && <CompareGrid payload={data} />}

        {data && (
          <div className="flex justify-end gap-2 pt-3 border-t">
            <Button
              variant="outline"
              onClick={onReject}
              disabled={resolving}
              className="text-red-600 hover:text-red-700"
            >
              <X className="w-4 h-4 mr-1" /> Reject
            </Button>
            <Button
              onClick={onAccept}
              disabled={resolving}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {resolving ? (
                <RotateCw className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Check className="w-4 h-4 mr-1" />
              )}
              Accept candidate
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ingText(ings: IngredientShape[]): string[] {
  return [...ings]
    .sort(
      (a, b) =>
        (a.sortOrder ?? a.sort_order ?? 0) -
        (b.sortOrder ?? b.sort_order ?? 0),
    )
    .map((i) => {
      const parts = [
        i.amount ?? null,
        i.unit ?? null,
        i.item,
        (i.prepNotes ?? i.prep_notes) ? `(${i.prepNotes ?? i.prep_notes})` : null,
      ].filter((x): x is string => Boolean(x && String(x).trim()));
      return parts.join(" ");
    });
}

function CompareGrid({ payload }: { payload: CandidatePayload }) {
  const cur = payload.currentRecipe;
  const cand = payload.candidate.candidateData.recipe;

  const fields: { label: string; current: string | null | undefined; next: string | null | undefined }[] = [
    { label: "Title", current: cur.title, next: cand.title },
    { label: "Author / Source", current: cur.authorSource, next: cand.author_source },
    { label: "Category", current: cur.category, next: cand.category },
    { label: "Cuisine", current: cur.cuisine, next: cand.cuisine },
    { label: "Prep time", current: cur.prepTime, next: cand.prep_time },
    { label: "Cook time", current: cur.cookTime, next: cand.cook_time },
    { label: "Total time", current: cur.totalTime, next: cand.total_time },
    { label: "Yield", current: cur.yieldServings, next: cand.yield_servings },
    { label: "Difficulty", current: cur.difficulty, next: cand.difficulty },
    { label: "Source type", current: cur.sourceType, next: cand.source_type },
  ];

  const curIngs = ingText(cur.ingredients ?? []);
  const candIngs = ingText(cand.ingredients ?? []);

  return (
    <div className="space-y-4">
      {payload.sourceImages.length > 0 && (
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-1.5">
            Source image{payload.sourceImages.length > 1 ? "s" : ""}
          </p>
          <div className="flex gap-2 flex-wrap">
            {payload.sourceImages.map((img) => (
              <img
                key={img.id}
                src={`${getBaseUrl()}/api/images/file/${img.filename}`}
                alt={img.originalName}
                className="w-24 h-24 object-cover rounded border"
              />
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 sticky top-0 bg-background z-10 py-1">
        <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
          Current
        </div>
        <div className="text-xs font-bold uppercase tracking-wide text-purple-700">
          New candidate
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1 border rounded-md overflow-hidden">
        {fields.map((f) => {
          const same = (f.current ?? "") === (f.next ?? "");
          return (
            <React.Fragment key={f.label}>
              <FieldCell label={f.label} value={f.current} highlight={!same} side="current" />
              <FieldCell label={f.label} value={f.next} highlight={!same} side="next" />
            </React.Fragment>
          );
        })}
      </div>

      <div>
        <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-1.5">
          Ingredients
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <IngredientList items={curIngs} other={candIngs} side="current" />
          <IngredientList items={candIngs} other={curIngs} side="next" />
        </div>
      </div>

      <div>
        <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-1.5">
          Directions
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <pre className="text-xs whitespace-pre-wrap font-sans bg-muted/40 border rounded p-3">
            {cur.directions || "—"}
          </pre>
          <pre className="text-xs whitespace-pre-wrap font-sans bg-purple-50 border border-purple-200 rounded p-3">
            {cand.directions || "—"}
          </pre>
        </div>
      </div>

      {(cur.handwrittenNotes || cand.handwritten_notes) && (
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-1.5">
            Handwritten notes
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <pre className="text-xs whitespace-pre-wrap font-sans bg-muted/40 border rounded p-3">
              {cur.handwrittenNotes || "—"}
            </pre>
            <pre className="text-xs whitespace-pre-wrap font-sans bg-purple-50 border border-purple-200 rounded p-3">
              {cand.handwritten_notes || "—"}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}

function FieldCell({
  label,
  value,
  highlight,
  side,
}: {
  label: string;
  value: string | null | undefined;
  highlight: boolean;
  side: "current" | "next";
}) {
  return (
    <div
      className={`px-3 py-2 text-sm border-b ${
        highlight
          ? side === "next"
            ? "bg-yellow-50"
            : "bg-orange-50"
          : ""
      }`}
    >
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className="text-sm">{value || <span className="text-muted-foreground">—</span>}</div>
    </div>
  );
}

function IngredientList({
  items,
  other,
  side,
}: {
  items: string[];
  other: string[];
  side: "current" | "next";
}) {
  const otherSet = new Set(other.map((s) => s.toLowerCase()));
  return (
    <ul className="text-xs border rounded p-2 bg-background space-y-0.5">
      {items.length === 0 && (
        <li className="text-muted-foreground">No ingredients</li>
      )}
      {items.map((line, i) => {
        const onlyHere = !otherSet.has(line.toLowerCase());
        return (
          <li
            key={i}
            className={`px-1 py-0.5 rounded ${
              onlyHere
                ? side === "next"
                  ? "bg-yellow-100"
                  : "bg-orange-100"
                : ""
            }`}
          >
            {line}
          </li>
        );
      })}
    </ul>
  );
}
